import type { GetServerSideProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Avatar, Wrap } from '@leafwell/components';
import WebPageJson from 'components/structured-data/webpage';
import ProductJson from 'components/structured-data/product';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getPageServerSide } from 'lib/queries/pages';
import { parse } from 'cookie';
import { POSTHOG_COOKIE_KEY } from 'lib/cookies/posthog-cookie';
import { default as posthogAPI } from 'lib/posthog';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';
import StickyMobileCta from 'components/sticky-mobile-cta';
import MedicalReviewer from 'components/medical-reviewer';

const HeroCard = dynamic(() => import('components/hero-card'));
const HeroCardNew = dynamic(() => import('components/hero-card-new'));
const Loader = dynamic(() => import('components/loader'));

const staticPage = [
  {
    id: 96686,
    date: '2026-02-08T20:32:08',
    modified: '2026-04-09T07:24:27',
    slug: 'florida',
    title: 'Florida (FL) Medical Marijuana Card',
    content: '',
    acf: {
      city: '',
      page_type: 'card',
      has_referral: true,
      hero: {
        title:
          'Enjoy <span class="text-dark-green">4 months FREE</span> on your Florida medical marijuana card!',
        rich_title: '',
        renew_title:
          'Renew your card at the <span class="text-dark-green">lowest price</span> in the state',
        sub_title: 'Coming Soon',
        form_id: '',
        form_type: 'partner',
        form_title: '',
        form_label: '',
        renew_form_content_items: [
          {
            content_items:
              '<span class="text-dark-green font-medium">Quick</span> and <span class="text-dark-green font-medium">easy</span> process',
          },
          {
            content_items:
              '<span class="text-dark-green">Money-back</span> guarantee',
          },
          {
            content_items:
              'Approval at home <span class="text-dark-green font-medium">in minutes</span>',
          },
          {
            content_items:
              '<span class="text-dark-green font-medium">Best value</span> in Florida',
          },
          {
            content_items:
              '<span class="text-dark-green font-medium">Access</span> to dispensaries',
          },
        ],
        form_submit_text: 'Get your card',
        form_submit_url: '',
        address: '',
        description:
          '<p>New to Leafwell? Get a medical card at one of our in-person events. <span class="text-dark-green font-medium">Enjoy the first 4 months on us!</span></p>\n',
        renew_description:
          '<p><strong>Ready to renew?</strong> Get started at $99 and continue your <span class="text-dark-green font-medium">medical card benefits</span></p>\n',
        image: null,
        image_alignment: 'right',
        steps: false,
        button: null,
        download_button: null,
        badges: [
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
        ],
        google_my_business_details: {
          medical_card_utm: '',
          cannabis_counseling_services_utm: '',
          phone_number: '',
        },
        webinar_members: false,
        quick_links: [
          {
            section: 'qualifying-conditions',
            title: 'Do I qualify?',
            description: 'Check the qualifying conditions within seconds.',
            icon: {
              id: 95920,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-qualify.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'cost-information',
            title: 'Affordable price',
            description: 'Risk-free process. Get approved or your money back.',
            icon: {
              id: 95921,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-affordable.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'quick-steps',
            title: 'Quick, easy process',
            description:
              'Book an appointment, consult with a doctor, and receive your certification in just a few minutes.',
            icon: {
              id: 95922,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-quick-easy.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
        ],
        latitude: '',
        longitude: '',
        opening_hours: false,
      },
      linktree: {
        categories: false,
      },
      redirect_medical_card: true,
      redirect_product_endpoint: 'https://app.leafwell.com/medical-card/',
      custom_state_code: '',
      pause_appointments: false,
      is_affiliate: false,
      affiliate_logo: null,
      affiliate_website: '',
      form_url: '',
      vpd_page: [],
      is_experiment: false,
      is_renewal: false,
      is_clinic: false,
      total_medical_card_price: '99',
      name: 'Get a Florida Medical Marijuana Card Online or In Person',
      description:
        'Ready for your Florida medical marijuana card? Book your new card or renewal appointment with us now and get approved quickly and hassle-free.',
      image_object_caption: 'Florida (FL) Medical Marijuana Card by Leafwell',
      product: {
        name: '',
        description: '',
        image_url: null,
      },
      state_product_image: null,
      is_vpd_landing: false,
      vpd_logo: null,
      price: {
        price: '',
        new_price: '',
        percentage_discount: '',
        discount_code: '',
        free_discount_code: '',
      },
      patient_renewal_dropdown: false,
      patient_renewal_dropdown_info:
        'Prior to your consultation, we will verify your status within the program',
      affiliate_page: [],
      webinar_info: {
        webinar_title: '',
        webinar_description: '',
        webinar_zoom_url: '',
      },
      vpd_partner_name: false,
      states: ['FL'],
      blocks: [
        {
          acf_fc_layout: 'google_reviews',
          section_id: 'google-reviews',
          instance_id: 'bc45789e',
          title: 'What our patients say',
          location: 'florida',
        },
        {
          acf_fc_layout: 'benefits_of_mmc',
          instance_id: '8e48d290',
          section_id: 'key-benefits-of-mmc',
          title: 'The key benefits of a medical marijuana card in Florida',
          access_to_dispensaries: {
            title: 'Access to dispensaries',
            description:
              '<p>Having a medical cannabis card is the only way to legally purchase, use, and possess cannabis in the state of Florida.</p>\n<p>Recreational use is illegal.</p>\n',
          },
          tax_savings: {
            title: 'Tax savings',
            description: '',
          },
          cultivation: {
            title: 'Cultivation',
            description: '',
          },
          higher_possession: {
            title: 'Higher possession',
            description:
              '<p>Medical card holders can <strong>possess 4 oz of cannabis products</strong>.</p>\n<p>Non-card holders cannot legally possess cannabis products.</p>\n',
          },
          access_to_products: {
            title: 'Access <strong>higher-potency</strong> cannabis products',
            description: '',
          },
          lower_age_restriction: {
            title:
              'Get legal cannabis <strong>at age 18+</strong>, rather than 21+',
            description:
              '<p>Patients 18+ can qualify for a medical cannabis card.</p>\n<p>Minors can also apply for a medical cannabis card if they have an approved caregiver.</p>\n',
          },
          legal_protection: {
            title: 'Enjoy <strong>legal protections</strong> for employment',
            description: '',
          },
        },
        {
          acf_fc_layout: 'cards_image',
          instance_id: '084a59e6',
          section_id: 'quick-steps',
          title: '4 easy steps to get your FL medical card',
          label: 'Get approved in minutes using Leafwell’s online platform.',
          label_position: 'below_title',
          list_numbered: true,
          cards_background_color: 'beige',
          cards: [
            {
              title: 'Sign up',
              description:
                '<p>We host in-person pop-up clinics across Florida. Book an event near you to secure your spot.</p>\n',
              image: {
                id: 96266,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-signup-yellow.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'sign up icon',
              },
            },
            {
              title: 'Get approved',
              description:
                '<p>Once approved, your doctor will upload your certificate to the state platform, the OMMU.</p>\n',
              image: {
                id: 96267,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-certificate-purple.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'certificate',
              },
            },
            {
              title: 'Submit your application',
              description:
                '<p>Log on to the OMMU website to complete your application and pay the $75 state fee.</p>\n',
              image: {
                id: 96268,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-upload-cert-green.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: '',
              },
            },
            {
              title: 'Get your card',
              description:
                '<p>As soon as your application is completed, you&#8217;ll receive email approval and can shop at a dispensary.</p>\n',
              image: {
                id: 96269,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-get-card-pink.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'card',
              },
            },
          ],
          description: '',
          button: '',
        },
        {
          acf_fc_layout: 'table',
          instance_id: 'e7c3b535',
          section_id: 'cost-information',
          display_product_state_image: false,
          title: 'How much does the Florida ID card cost?',
          title_tag: 'h2',
          intro_text:
            '<p>Medical patients get so much more with Leafwell.  Our offer includes:</p>\n<ul>\n<li><strong>a medical card</strong></li>\n<li><strong>up to 30% off at in-network dispensaries</strong> throughout Florida.</li>\n<li><strong>50% cannabis care consultations</strong> with a Leafwell pharmacist</li>\n</ul>\n<p>Plus: get your money back if not approved.</p>\n<h3>New to Leafwell:</h3>\n<ul>\n<li>New 7-month certificate: First 4 months free, then $17/month*</li>\n<li>Renewal (7 months): First 4 months free, then $17/month*</li>\n</ul>\n<p>&nbsp;</p>\n<h3>Returning patient with Leafwell:</h3>\n<ul>\n<li>Renewal (7 months): $99 or $17/month*</li>\n</ul>\n<p>&nbsp;</p>\n<p><span style="font-size: 15px;">*state application fee applies: $75-77.25</span></p>\n',
          table: {
            columns: false,
          },
          button: {
            title: 'Get your card',
            url: '#leafwell-signup-form',
            target: '',
          },
        },
        {
          acf_fc_layout: 'content_and_banner',
          instance_id: 'f6e417c1',
          section_id: 'qualifying-conditions',
          title: 'How to qualify for a Florida med card',
          background_color: 'beige',
          content_left:
            '<p>Adults at least 18 experiencing at least one qualifying medical condition may be eligible to apply for a Florida medical marijuana card.</p>\n<p>Existing Leafwell patients can renew their medical marijuana cards online. Patients new to Leafwell must see a healthcare provider in person.  Stay tuned for upcoming events!</p>\n<p>The following medical conditions qualify patients for medical marijuana recommendation in the state of Florida:</p>\n<ul>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/hiv-aids">AIDS/HIV</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/lou-gehrigs-disease-aka-amyotrophic-lateral-sclerosis-als">Amyotrophic Lateral Sclerosis (ALS)</a> aka Lou Gehrig&#8217;s Disease</li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/cancer">Cancer</a></li>\n<li><a href="https://leafwell.com/medical-conditions/chronic-pain-muscle-pain">Chronic Pain</a> (nonmalignant) caused by a qualifying medical condition or that originates from a qualifying medical condition and persists beyond the usual course of that qualifying medical condition.</li>\n</ul>\n',
          content_right:
            '<ul class="-mt-4 lg:mt-0">\n<li><a href="https://leafwell.com/medical-conditions/crohns-disease">Crohn&#8217;s Disease</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/epilepsy">Epilepsy</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/glaucoma">Glaucoma</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/multiple-sclerosis">Multiple Sclerosis (MS)</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/parkinsons-disease">Parkinson&#8217;s Disease (PD)</a></li>\n<li aria-level="1"><a href="https://leafwell.com/medical-conditions/post-traumatic-stress-disorder-ptsd">Post-Traumatic Stress Disorder (PTSD)</a></li>\n<li aria-level="1">A terminal condition diagnosed by a physician other than the qualified physician issuing the medical marijuana certification.</li>\n<li aria-level="1">Medical conditions of the same kind or class as or comparable to those above.</li>\n</ul>\n',
          banner_title: 'Convenient, Quality Care<br/> When You Need It Most',
          banner_label:
            "With Leafwell, you'll be matched up with a highly knowledgeable, empathetic, licensed medical marijuana doctor or provider.",
          banner_image: {
            id: 76039,
            src: 'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png',
            width: 1568,
            height: 750,
            srcSet:
              'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-700x335.png 700w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1024x490.png 1024w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-400x191.png 400w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-768x367.png 768w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1536x735.png 1536w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png 1568w',
            alt: 'health care providers',
          },
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'a9a5bb00',
          container_size: 'default',
          content:
            '<h2>2 documents needed to apply for a Florida medical marijuana card</h2>\n<p>To apply for a medical marijuana card in Florida, you must follow a few essential steps to ensure your eligibility and successful registration.</p>\n<ol>\n<li><strong>Proof of Identity</strong><br />\nYou will need a government-issued photo ID, such as a Florida driver&#8217;s license or Florida state identification card. You may also submit an out-of-state driver’s license or state identification card with two proofs of Florida residency (for seasonal Florida residents). For minors,  a certified birth certificate or FL school registration record, AND legal guardian’s FL Driver’s License or State ID.</li>\n<li><strong>Medical Marijuana Recommendation</strong><br />\nOnce approved, you will receive two emails from the state. One includes your username (email), and the other includes a temporary password that expires in 24 hours. Use those credentials to log into the<a href="https://mmuregistry.flhealth.gov/Public/LogIn"> Florida Medical Marijuana Program website</a> to complete your online application.</li>\n</ol>\n<div data-highlight="button" data-label="Get My Card" data-ref="#leafwell-signup-form" data-target="_self"></div>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '9419bf53',
          container_size: 'default',
          content:
            '<h2>Next steps after approval</h2>\n<p>Congratulations on getting approved for your medical certification. Here&#8217;s a guide to what you need to do next.</p>\n<h4>Have the following ready before you begin your application:</h4>\n<ul>\n<li>Photo ID\n<ul>\n<li>The Registry is connected to the Florida Department of Highway Safety and Motor Vehicles’ (FLHSMV) demographic database. If there is a match to the information in your profile, your state ID or driver’s license photo can be automatically added to your application and approved.</li>\n</ul>\n</li>\n<li>Proof of <a href="https://s27415.pcdn.co/wp-content/uploads/2020/02/Required-POR-Documentation.pdf">Residency Documentation</a></li>\n<li>$75 payment fee (Plus $2.75 convenience fee)</li>\n</ul>\n<h4>Here is the process for applying in Florida</h4>\n<p>For detailed instructions with images, <a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/Instructional_Guides/PT/Application-Initial-Instructions.pdf">click here</a>.</p>\n<ol>\n<li>After your appointment with a Leafwell physician, you will receive 2x emails with the subject line &#8220;Medical Marijuana Use Registry New User Account&#8221; (<a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/Instructional_Guides/PT/Login-Instructions-Initial.pdf">here&#8217;s a walkthrough for step 1</a>).\n<ol style="list-style-type: lower-alpha;">\n<li>One email contains your username (your email address).</li>\n<li>The second email contains a temporary password &#8211; these passwords are case-sensitive.</li>\n<li>Click the link in the email to log into the Medical Marijuana Use Registry <a href="https://mmuregistry.flhealth.gov/">https://mmuregistry.flhealth.gov/</a>.</li>\n<li>Click login in the top left, enter your email and password, and click the log in button.</li>\n<li>You will then be able to set your password &#8211; type the temporary password into the &#8216;current password&#8217; box then set your own.</li>\n<li>Once this is done, you will be automatically redirected to your registry homepage.</li>\n</ol>\n</li>\n<li>Click on &#8220;<strong>Your Card</strong>&#8221; and then click &#8220;<strong>Begin Initial Application</strong>.&#8221;</li>\n<li>Click &#8220;<strong>Supply a Photo</strong>&#8221; to supply either your State ID photo or a passport-style photo to your application.</li>\n<li>Scroll down to the &#8220;Proof of Residence&#8221; section and click &#8220;<strong>Check State Residency Status</strong>&#8221; to obtain your Driver&#8217;s License or Florida State ID from FLHSMV, or click &#8220;<strong>Supply My Own</strong>&#8221; to supply your own proof of residency document.</li>\n<li>Review all data to make sure it is accurate. Then click &#8220;<strong>Apply</strong>.&#8221;</li>\n<li>Electronically sign your application and click &#8220;<strong>Submit My Card Application</strong>.&#8221;</li>\n<li>Pay the application fee.</li>\n<li>Click &#8220;<strong>Print for your Records</strong>&#8221; to print the payment confirmation for your records.</li>\n<li>The entire application review process takes a total of 10 business days from the date of submission. Once the application is approved you will receive an electronic temporary card to use while the physical official card is printed. Within 5 business days, the official card is mailed to you.</li>\n</ol>\n<h4>Additional Resources:</h4>\n<ul>\n<li><a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/Instructional_Guides/PT/Login-Instructions-Initial.pdf">Logging in for the first time</a></li>\n<li><a href="https://knowthefactsmmj.com/registry/#instructional-guides">Instructional Guides</a></li>\n</ul>\n',
          section_id: 'state-steps',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'ef00c887',
          container_size: 'default',
          content:
            '<h2>How to renew your medical cannabis ID card in Florida</h2>\n<p>It’s easy to renew your Florida medical marijuana card with Leafwell.</p>\n<p><mark></mark>You can renew up to 45 days before your card expires, and Florida patients can only be seen by their previously approved provider via telemedicine.</p>\n<p><strong>Renewal Application Process:</strong></p>\n<ol>\n<li>Sign in to your existing Leafwell account.</li>\n<li>Update your renewal registration and book an appointment.</li>\n<li>After getting approved, the provider will refill your medication order.*</li>\n<li>If your state card is due to expire, log into the <a href="https://mmuregistry.flhealth.gov/" rel="noopener noreferrer nofollow">Florida Medical Marijuana Program</a> to complete the state-side renewal application.</li>\n<li>Receive email approval from the Florida state agency and legally purchase cannabis from a licensed dispensary.</li>\n</ol>\n',
          section_id: 'renew',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '30bf5447',
          container_size: 'default',
          content:
            '<h2>I&#8217;m new to Leafwell. Is there anything I need to do before renewing my Florida medical marijuana card/certification?</h2>\n<p>All existing Florida card holders who are new to Leafwell must self-deactivate and leave their current physician in the Medical Marijuana Use Registry before the consultation. If you are not changing doctors from your last visit, there is no need to self-deactivate.</p>\n<p>Your new physician won’t be able to activate you under their care and complete your certification until you’ve self-deactivated, so it’s important to complete this step before your appointment to avoid delays.</p>\n<p>Follow these steps to leave your current physician:</p>\n<ol>\n<li>Log into the Medical Marijuana Use Registry <a href="https://mmuregistry.flhealth.gov/">https://mmuregistry.flhealth.gov/</a>.</li>\n<li>Navigate to the &#8220;YOUR PROFILE&#8221; menu option at the top of the page to view your information.</li>\n<li>Scroll to the bottom of your profile page, then click &#8220;LEAVE YOUR CURRENT PHYSICIAN.&#8221;\n<ol style="list-style-type: lower-alpha;">\n<li>If the &#8220;Current Physician&#8221; states &#8220;No Current Physician,&#8221; or you do not have a button for &#8220;LEAVE YOUR CURRENT PHYSICIAN,&#8221; you are not currently active with a qualified physician.</li>\n</ol>\n</li>\n<li>Click &#8220;Confirm&#8221; to save your changes.</li>\n</ol>\n<p>The state guide — with screenshots — can be found <a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/Instructional_Guides/PT/Deactivate-by-Patient.pdf">here</a>.</p>\n',
          section_id: 'disconnect',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '23ddfc2b',
          container_size: 'default',
          content:
            '<h2>Next steps after renewal</h2>\n<h4>Before you start, have these supporting documents ready:</h4>\n<ul>\n<li>Leafwell Physician Certificate</li>\n<li>A debit or credit card</li>\n</ul>\n<h4>Renewing Your Florida Medical Marijuana Card</h4>\n<p>Here&#8217;s the <a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/Instructional_Guides/PT/Application-Renewal-Instructions.pdf">state&#8217;s official guide, including photos</a> &#8211; or you can read ours below too.</p>\n<ol>\n<li>Log into the Medical Marijuana Use Registry: <a href="https://mmuregistry.flhealth.gov/">https://mmuregistry.flhealth.gov/</a></li>\n<li>Navigate to the &#8220;<strong>YOUR CARD</strong>&#8221; menu option at the top of the page to view your Application History page.</li>\n<li>Click &#8220;<strong>BEGIN RENEWAL</strong>&#8221; to open a renewal application. You may start a renewal application beginning 45 days prior to your card’s expiration date. The number of days until you can begin a renewal application will be listed on this page. If your previous card is still valid, renewing your card will not affect your ability to obtain products.</li>\n<li>Click &#8220;<strong>PROCEED</strong>.&#8221;</li>\n<li>Review all data to make sure it is accurate, then click &#8220;<strong>APPLY</strong>.&#8221;</li>\n<li>Read the confirmation message and click &#8220;<strong>APPLY</strong>&#8221; to confirm.</li>\n<li>Click &#8220;<strong>SUPPLY A PHOTO</strong>&#8221; to supply either your Florida State ID photo or your own passport-style photo.\n<ol style="list-style-type: lower-alpha;">\n<li>Clicking &#8220;CHECK STATE ID PHOTO&#8221; will upload your driver’s license photo from the Florida Department of High Safety and Motor Vehicles’ (FLHSMV) demographic database.</li>\n<li>Clicking &#8220;SUPPLY MY OWN&#8221; will allow you to upload a photo from your device. (Using the FLHSMV sourced data will automatically approve the photo and may cut down on processing time, while supplying your own photo may increase the processing time.)</li>\n</ol>\n</li>\n<li>Scroll down to the &#8220;<strong>PROOF OF RESIDENCE</strong>&#8221; section and then click &#8220;<strong>SUPPLY PROOF</strong>.&#8221;</li>\n<li>Click &#8220;<strong>CHECK STATE RESIDENCY STATUS</strong>&#8221; to obtain your Driver&#8217;s License or Florida State ID from FLHSMV. If you wish to upload your own photo, click &#8220;<strong>SUPPLY MY OWN</strong>&#8221; to supply your own proof of residency document. Select the file you wish to upload and click &#8220;<strong>OPEN</strong>.&#8221; The document will appear below the &#8220;<strong>SUPPLY PROOF</strong>&#8221; button.</li>\n<li>Review your information for accuracy, then click &#8220;<strong>APPLY</strong>.&#8221;</li>\n<li>Electronically sign your application. Scroll to the bottom of the page to the Signature section to type in your first name and last name in the boxes provided.</li>\n<li>Click &#8220;<strong>SUBMIT MY CARD APPLICATION</strong>.&#8221;</li>\n<li>Electronically sign your application and click &#8220;<strong>Submit My Card Application</strong>.&#8221;</li>\n<li>Pay the application fee.</li>\n<li>Click &#8220;<strong>Print for your Records</strong>&#8221; to print the payment confirmation for your records.</li>\n<li>The current processing time for the new application review process is 10 business days from the date of submission. For renewals, this should be instant. Payments take (5) business days to fully process and post in the Medical Marijuana Use Registry. Once your payment is processed, the application review period can take up to an additional 5 business days. If any part of an application is rejected or missing or invalid documentation, the processing time will be delayed.</li>\n</ol>\n',
          section_id: 'state-renewal',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '4809c6cc',
          container_size: 'default',
          content:
            '<h2>Legal limitations for Florida cardholders</h2>\n<p>Navigating the legal landscape can be tricky, but we’re here to break it down for you. Here are some key points you should know if you have a medical marijuana ID card in Florida.</p>\n<p><b>No Public Consumption:</b> Patients can not consume cannabis in public places or on public transportation.</p>\n<p><b>Cultivation is Off-Limits</b>: Patients cannot cultivate cannabis plants. Growing a marijuana plant even as a medical patient is not permitted.</p>\n<p><b>Medical Marijuana Treatment Centers:</b> Licensed medical marijuana treatment centers (MMTCs) are vertically integrated and are the only businesses in Florida authorized to dispense medical marijuana and low-THC cannabis to qualified patients and caregivers. They are also commonly referred to as medical marijuana dispensaries or clinics.</p>\n<p><b>Daily Dose Amount Limit:</b> According to a 2022 emergency rule &#8220;a qualified physician may not issue a physician certification for more than three 70-day supply limits of marijuana or more than six 35-day supply limits of marijuana in a form for smoking.&#8221;</p>\n<p><b>Recreational Use Is Prohibited:</b> Florida Medical Marijuana Laws prohibit anyone in Florida to possess marijuana flower or low-THC cannabis unless it is sealed in a tamper-proof container sold at a medical marijuana clinic.</p>\n<p><strong>No Gun Permit If You Have a Medical Card</strong>: Medical marijuana use prohibits you from obtaining a weapons permit in Florida, as the federal government considers medical marijuana as a Schedule 1 controlled substance, which is against federal law.</p>\n',
          section_id: 'laws',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '43122281',
          container_size: 'default',
          content:
            '<h2>How to become a caregiver in Florida</h2>\n<p>If you’re looking to become a medical marijuana caregiver for a medical marijuana patient in Florida, there are some important rules and steps to follow.</p>\n<p><b>Picking Up for Patients</b></p>\n<p>As a caregiver, you can pick up medicine from a medical marijuana dispensary on behalf of a patient with terminal illnesses or in hospice care.</p>\n<p><b>Caregiver Limitations</b></p>\n<p>If a caregiver is not a close relative of the qualified patient they are assisting, they must also pass a background screening. If the patient is a minor, having a caregiver is a must.</p>\n<p><b>Application Process</b></p>\n<p>When applying to become a caregiver, applicants must provide the following:</p>\n<ul>\n<li aria-level="1">A completed<a href="https://knowthefactsmmj.com/wp-content/uploads/_documents/NOC_FINAL_DH8010-OCU-03.2018.pdf"> caregiver application</a>.</li>\n<li aria-level="1">A copy of the caregiver&#8217;s proof of residency.</li>\n<li aria-level="1">A $75 check or money order to cover the application fee payable to the Florida Department of Health.\n<ul>\n<li aria-level="2">A $2.75 convenience fee applies to online payments.</li>\n</ul>\n</li>\n<li aria-level="1">A 2×2-inch photograph taken within 90 days. The photo must be passport style, in color, and show the caregiver&#8217;s full face.</li>\n</ul>\n<p><b>Note:</b> Your Medical Marijuana Registry ID card expires after one year and must be renewed 45 days before expiration.</p>\n<p><b>Eligibility Criteria</b></p>\n<p>According to the<a href="https://s27415.pcdn.co/wp-content/uploads/_documents/NOC_FINAL_DH8010-OCU-03.2018.pdf"> Office of Medical Marijuana Use</a>, medical marijuana caregivers in Florida must:</p>\n<ul>\n<li aria-level="1">Be 21 years of age.</li>\n<li aria-level="1">Be a Florida resident.</li>\n<li aria-level="1">Agree in writing to assist with the qualified patient&#8217;s medical use of marijuana.</li>\n<li aria-level="1">Be registered in the Medical Marijuana Use Registry of no more than one qualified patient.</li>\n<li aria-level="1">Complete a caregiver certification course provided by the Florida Department of Health or its designee.</li>\n</ul>\n<p>If a caregiver is not a close relative of the qualified patient they are assisting, they must also pass a background screening.</p>\n',
          section_id: 'caregiver',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '4bd813a5',
          container_size: 'default',
          content:
            '<h2>Can I use my Florida medical card in other states?</h2>\n<p>Florida does not offer medical marijuana reciprocity to visitors with a medical card from other states.</p>\n<p>That said, these states will recognize Florida medical marijuana cards:</p>\n<ul>\n<li>Arizona</li>\n<li>Arkansas *</li>\n<li>Delaware</li>\n<li>Hawaii *</li>\n<li>Louisiana</li>\n<li>Maine</li>\n<li>Michigan (at the dispensary&#8217;s discretion)</li>\n<li>Nevada</li>\n<li>New Hampshire</li>\n<li>New Jersey *</li>\n<li>New Mexico</li>\n<li>Oklahoma *</li>\n<li>Puerto Rico</li>\n<li>Rhode Island</li>\n<li>U.S. Virgin Islands *</li>\n<li>Utah * (up to two 21-day periods in a calendar year)</li>\n<li>Washington, D.C.</li>\n</ul>\n<p>*Visitors must complete a visiting patient application with the state program.</p>\n',
          section_id: 'reciprocity',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'ab13b3ad',
          container_size: 'default',
          content:
            '<h2>Minor patient information</h2>\n<p>If a qualified patient is younger than 18 years of age, only a caregiver may purchase or administer medical marijuana for the qualified patient. Patients who are minors may not purchase marijuana.</p>\n<p>The patient&#8217;s qualified physician is the only person able to add a caregiver to a patient&#8217;s profile in the Medical Marijuana Use Registry. See the Caregivers section for requirements.</p>\n<p>Both adult and minor patients should follow the Steps to Medical Marijuana Treatment. The first step is to visit a qualified physician. However, Florida law has specific requirements for the medical use of marijuana by minors. These requirements include:</p>\n<p>A qualified physician must determine that the medical use of marijuana would likely outweigh the potential health risks for all patients.</p>\n<p>If the patient is younger than 18 years of age, a second physician must concur with this determination and such concurrence must be documented in the patient’s medical record.</p>\n<p>The parent or legal guardian of a qualified minor patient must provide the Office of Medical Marijuana Use (OMMU) with written consent for the patient to use medical marijuana.</p>\n<p>The patient’s qualified physician must submit the parent’s/guardian’s Consent for Minor Patient (CMP) form to the OMMU prior to the minor patient beginning an MMUR ID Card application.</p>\n<p>Qualified medical marijuana patients who are under the age of 18, must designate a caregiver in the qualified patient’s application and in the Medical Marijuana Use Registry. Designated caregivers must also submit a completed application to the OMMU to obtain a Medical Marijuana Use Registry Caregiver identification card.</p>\n<p>Medical marijuana patients under the age of 18 may not obtain a certification for marijuana for medical use by smoking unless the patient is diagnosed with a terminal condition and a second physician, who is a board-certified pediatrician, agrees with the determination.</p>\n',
          section_id: 'minor',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'faqs',
          instance_id: '4e2425c5',
          section_id: '',
          title: 'FAQs',
          description:
            '<p>Learn the answers to FAQs about medical marijuana in Florida.</p>\n',
          items: [
            {
              id: 81660,
              title: 'Can I renew my Florida medical marijuana card online?',
              content:
                'You can get a medical cannabis certificate online with Leafwell, but renewals only - not new certifications.',
            },
            {
              id: 81662,
              title:
                'How often do I need to renew my Florida medical marijuana card?',
              content:
                'Florida medical marijuana certificates are valid for 30 weeks. Renewals may be done online, but only with the same certifying medical provider. We recommend renewing 45 days before your expiration date to avoid a lapse in coverage.',
            },
            {
              id: 81664,
              title: 'What are the medical marijuana laws in Florida?',
              content:
                "<ul>\r\n \t<li>2014 - CBD oil is legalized. Florida Amendment 2, which would have legalized medical cannabis, fails.</li>\r\n \t<li>2016 - In March 2016, Gov. Scott signed into law House Bill 307 to expand the state's Right to Try Act (allowing the use of certain drugs for terminally ill patients) to include the use of cannabis.The bill also sought to address problems that had arisen with the implementation of the state's CBD law that was approved in 2014.[15] House Bill 307 passed the House 99–16 and the Senate 28–11. Amendment 2 is approved.</li>\r\n \t<li>2019 - A bill that removed the prohibition on smoking was signed into law in March 2019.</li>\r\n</ul>",
            },
            {
              id: 81666,
              title:
                'Will I be added to a public database once I have my medical marijuana card in Florida?',
              content:
                'Patients must enroll in the Florida Medical Marijuana Use Registry in order to legally access medical cannabis. However, this registry is not public.',
            },
            {
              id: 81668,
              title: 'How do I buy medical cannabis in Florida?',
              content:
                "If you have a medical marijuana card and physician's certificate, you can buy cannabis in Florida at a licensed dispensary or use a licensed delivery service.",
            },
            {
              id: 65829,
              title: 'Is it hard to get a medical marijuana card in Florida?',
              content:
                "No, it's not difficult to get certified for a medical marijuana card in Florida, especially with Leafwell. Speak to one of our physicians today! You must renew your card 45 days before it expires, and Florida patients can only be seen by their previously approved provider via telemedicine.",
            },
            {
              id: 83817,
              title:
                'Can I get a medical marijuana card the same day in Florida?',
              content:
                'After submitting your medical cannabis registration to the state, you should receive a digital ID number within a few hours. This can be used to purchase cannabis at licensed medical marijuana treatment centers. After that, it can take up to 10 business days to receive your physical medical cannabis card through the mail.',
            },
            {
              id: 83820,
              title: 'Can a job deny me if I have a medical card in Florida?',
              content:
                'Yes. Florida state law does not offer job protection for medical marijuana use, and employers may require a drug test to maintain a drug-free workplace. This means that it is possible for a medical marijuana patient to be denied a job.',
            },
            {
              id: 83824,
              title: 'Can I own a gun and have a medical card in Florida?',
              content:
                'In Florida, the laws surrounding gun ownership and having a medical marijuana card at the same time are complex. Governor Ron DeSantis of Florida signed HB 543 into law, officially making Florida the 26th constitutional carry state. The bill permits qualified adults who are 21 years and older to carry concealed firearms without requiring a permit. Adults with a medical marijuana card who meet the requirements outlined in Florida Statute 790.06 are eligible to conceal a firearm without requiring a permit.',
            },
            {
              id: 83826,
              title: 'Can employers see if I have a medical card in Florida?',
              content:
                "Your status as a card holder will not show up in any background check. However, employers may request a drug test to maintain a drug-free workplace, and medical marijuana patients may be denied or accepted employment at the employer's discretion.",
            },
            {
              id: 83828,
              title: 'Can I have a medical card and be a nurse in FL?',
              content:
                "It's possible to get a medical marijuana card as a nurse in Florida. However, due to drug-testing protocol, nurses who have a card put their license to practice on the line. Those who test positive for THC or its metabolites may have their licenses revoked, or not granted in the first place.",
            },
            {
              id: 83830,
              title: 'Can a tourist get a medical card in Florida?',
              content:
                'No, it is not possible for a tourist to qualify for a medical marijuana card in <a href="https://newdelz.com/">Florida</a>. A Florida MMJ card is granted by the state, and only those who are Florida residents may qualify for medical cannabis.',
            },
            {
              id: 83832,
              title: 'Can I get a temp medical card in Florida?',
              content:
                'If you have applied for a medical marijuana card and have been approved, you will receive a temporary electronic card that can be used while the official physical card is printed. This temporary card is usually available within 10 days.',
            },
            {
              id: 83822,
              title: 'How fast can I get a medical card in Florida?',
              content:
                "If you attend an in-person event for an initial certification, you can typically receive your approval email from the state within 2 hours or less after completing your appointment.\r\n\r\nOnce you have paid the state fee and completed your registration, you'll receive a patient ID number, which allows you to start shopping at licensed dispensaries the same day.",
            },
          ],
          button: '',
        },
        {
          acf_fc_layout: 'table_of_contents',
          instance_id: '624632f5',
          table_of_contents: [
            {
              label: 'What our patients say',
              id: 'google-reviews',
            },
            {
              label: 'What are the benefits of a medical marijuana card?',
              id: 'key-benefits-of-mmc',
            },
            {
              label: 'Quick steps',
              id: 'quick-steps',
            },
            {
              label: 'How much does the card cost?',
              id: 'cost-information',
            },
            {
              label: 'How to qualify for a card',
              id: 'qualifying-conditions',
            },
            {
              label: '3 things you need to apply',
              id: 'what-you-need',
            },
            {
              label: 'Next steps after approval',
              id: 'state-steps',
            },
            {
              label: 'How to renew your card in Florida',
              id: 'renew',
            },
            {
              label: 'Next steps after renewal',
              id: 'state-renewal',
            },
            {
              label: 'Legal limitations for Florida cardholders',
              id: 'laws',
            },
            {
              label: 'How to become a caregiver in Florida',
              id: 'caregiver',
            },
            {
              label: 'Can I use my Florida medical card in other states?',
              id: 'reciprocity',
            },
            {
              label: 'Minor patient information',
              id: 'minor',
            },
          ],
        },
      ],
    },
    seo: {
      title:
        'How to get & renew a medical marijuana card in Florida - Leafwell',
      description:
        'Get or renew your FL medical marijuana card with Leafwell. First 4 months free - Don’t miss this special offer!',
      canonical: 'https://leafwell.com/medical-card/florida',
      images: [
        {
          source_url:
            'https://admin.leafwell.com/app/uploads/2025/05/medical-card-florida.jpg',
          alt_text: 'person holding a Florida medical card',
          media_details: {
            width: 637,
            height: 637,
          },
        },
      ],
      meta_robots_noarchive: false,
      meta_robots_nofollow: false,
      meta_robots_noindex: false,
      opengraph: {
        title:
          'How to get & renew a medical marijuana card in Florida - Leafwell',
        description:
          'Get or renew your FL medical marijuana card with Leafwell. First 4 months free - Don’t miss this special offer!',
        type: 'website',
      },
      redirect: null,
    },
    language: 'en',
    translations: [
      {
        uri: '/medical-card/florida',
        slug: 'en',
        locale: 'en',
      },
    ],
  },
];

const PageContent: React.FC<PageProps> = ({
  page: {
    title,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectProductEndpoint,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      city,
      hasReferral,
      isClinic,
    },
  },
  settings: { seoSettings },
  variant,
  featureFlag,
  inPersonEvents,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      });
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);

  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      <WebPageJson
        seo={seo}
        date={date}
        title={title}
        modified={modified}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
        siteUrl={seoSettings.siteUrl}
        name={name}
        description={description}
        imageObjectCaption={imageObjectCaption}
        siteDescription={seoSettings.homepage.homepageDescription}
      />
      <ProductJson
        product={product}
        totalMedicalCardPrice={totalMedicalCardPrice}
      />
      {variant === 'test' ? (
        <>
          <HeroCardNew
            hero={hero}
            states={states}
            affiliateLogo={affiliateLogo}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            inPersonEvents={inPersonEvents}
            passwordless={true}
          />
          <StickyMobileCta
            observeTargetId="hero-cta-sentinel"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      ) : (
        <HeroCard
          hero={hero}
          pageType={pageType}
          states={states}
          formUrl={formUrl}
          affiliateLogo={affiliateLogo}
          affiliateWebsite={affiliateWebsite}
          pauseAppointments={pauseAppointments}
          redirectMedicalCard={redirectMedicalCard}
          redirectCannabisCounseling={redirectCannabisCounseling}
          totalMedicalCardPrice={totalMedicalCardPrice}
          city={city}
          isClinic={isClinic}
          redirectProductEndpoint={redirectProductEndpoint}
          inPersonEvents={inPersonEvents}
        />
      )}
      {blocks && blocks.length > 0 ? (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
          variant={variant}
        />
      ) : null}
      <MedicalReviewer modified={modified} />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getServerSideProps = (async context => {
  const { req } = context;
  const cookies = parse(req.headers.cookie || '');

  let distinctId: string;
  try {
    const phCookie = cookies[POSTHOG_COOKIE_KEY];
    distinctId = phCookie
      ? JSON.parse(phCookie).distinct_id
      : crypto.randomUUID();
  } catch {
    distinctId = crypto.randomUUID();
  }

  const bootstrapData = await posthogAPI.getFeatureFlags(distinctId, {
    $pathname: '/medical-card/florida',
  });
  const newStatePageDesign =
    bootstrapData?.featureFlags?.['website-new-state-page-design-FL'] ??
    'control';

  return await getPageServerSide({
    ...context,
    variant: newStatePageDesign,
    featureFlag: 'website-new-state-page-design-FL',
    slug: 'medical-card/florida',
    staticPage: camelcaseKeys(staticPage[0], { deep: true }),
  });
}) satisfies GetServerSideProps<PageProps>;

export default Page;
